/**
 * EmailInviteModal - Add human members via email (no token)
 *
 * Workflow:
 * 1. Travis enters email address
 * 2. Creates pending membership in Firestore
 * 3. Copies standardized onboarding text to clipboard
 * 4. New user signs in with that email → auto-confirmed
 */

import { useState } from 'react';
import { auth } from '../../lib/firebase';
import { getAuthHeader } from '../../lib/auth';

interface EmailInviteModalProps {
  workspaceId: string;
  onClose: () => void;
}

export function EmailInviteModal({ workspaceId, onClose }: EmailInviteModalProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Auto-populate display name from email
  function handleEmailChange(newEmail: string) {
    setEmail(newEmail);

    // Extract first name from email if possible (e.g., john.doe@example.com → John)
    const localPart = newEmail.split('@')[0];
    if (localPart) {
      // Split by common separators and take first part
      const firstName = localPart.split(/[._-]/)[0];
      // Capitalize first letter
      const capitalized = firstName.charAt(0).toUpperCase() + firstName.slice(1);
      setDisplayName(capitalized);
    }
  }

  async function handleAddEmail() {
    setError(null);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be signed in to invite members');
      }

      // Create pending membership via backend API (uses Admin SDK to bypass security rules)
      const headers = {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
      };
      const response = await fetch('/api/members/create-pending', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          workspaceId,
          email: email.toLowerCase(),
          displayName: displayName.trim() || undefined,
          inviterId: currentUser.uid,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create pending member');
      }

      setSuccess(true);
      setEmail('');
      setDisplayName('');
    } catch (err) {
      console.error('[EmailInviteModal] Error adding pending member:', err);
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setLoading(false);
    }
  }

  function handleCopyInvite() {
    const inviteText = `You've been invited to join a Relay workspace — a platform where humans and AI work together as a team. To get started: go to https://easyrelay.app and sign in with your Google account or email address. Use the email address your team lead used to register you. Once you sign in, Relay will recognize you and bring you directly into the workspace. No code needed — just sign in and Relay will take over from there with a short introduction to get you set up.`;

    navigator.clipboard.writeText(inviteText);

    // Show brief confirmation
    const btn = document.getElementById('copy-invite-btn');
    if (btn) {
      const originalText = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 8,
          padding: 24,
          width: '100%',
          maxWidth: 480,
          border: '1px solid var(--border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: 'var(--text)',
            margin: '0 0 16px 0',
          }}
        >
          Add Human Partner
        </h2>

        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="invite-email"
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 8,
            }}
          >
            Email Address *
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="colleague@example.com"
            disabled={loading}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              backgroundColor: 'var(--bg)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="invite-displayname"
            style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 8,
            }}
          >
            Display Name
          </label>
          <input
            id="invite-displayname"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How they'll appear in the workspace"
            disabled={loading}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              backgroundColor: 'var(--bg)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button
          onClick={handleAddEmail}
          disabled={loading || !email}
          style={{
            width: '100%',
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 500,
            background: 'var(--accent)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: loading || !email ? 'not-allowed' : 'pointer',
            opacity: loading || !email ? 0.6 : 1,
            marginBottom: 16,
          }}
        >
          {loading ? 'Adding...' : 'Add to Workspace'}
        </button>

        {success && (
          <div
            style={{
              padding: 12,
              background: 'var(--tag-resolved-bg)',
              color: 'var(--tag-resolved)',
              borderRadius: 4,
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            ✓ Member added successfully! Now copy the invite text below and send it to them.
          </div>
        )}

        {error && (
          <div
            style={{
              padding: 12,
              background: 'var(--tag-blocker-bg)',
              color: 'var(--tag-blocker)',
              borderRadius: 4,
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            padding: 16,
            background: 'var(--bg)',
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--text)',
              marginBottom: 8,
            }}
          >
            Invite Instructions
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text2)',
              lineHeight: 1.5,
            }}
          >
            After adding a member, use the button below to copy standardized onboarding
            instructions. Send this text to your new team member via your preferred
            communication channel (email, Slack, etc.).
          </div>
        </div>

        <button
          id="copy-invite-btn"
          onClick={handleCopyInvite}
          style={{
            width: '100%',
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 500,
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: 'pointer',
            marginBottom: 16,
          }}
        >
          Copy Invite Text
        </button>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 500,
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
